import React, { useContext } from 'react';
import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { faFileAlt, faList } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@comparethemarketau/manor-button';
import { getBreakpointFromManor } from 'ctm-components';

import { withQuoteFlatten } from '../../products/products/common/with_quote_flatten';
import { SectionTitle } from '../common/section_title';
import { ContentCard } from '../common/content_card';
import { ContentTitleText } from '../common/content_title_text';
import { IsThisProductSuitableForMe } from '../../products/products/common/is_this_product_suitable_for_me';

const useStyles = createUseStyles((theme) => ({
    container: {
        marginBottom: theme.spacing[40]
    },
    content: {
        display: 'block',
        '& $ul ': {
            listStyleType: 'disc',
            listStylePosition: 'outside',
            '& li': {
                marginLeft: theme.spacing[24],
                lineHeight: '2.2rem'
            },
            '& $ul ': {
                listStyleType: 'circle',
                listStylePosition: 'outside',
                '& li': {
                    marginLeft: theme.spacing[48]
                },
                '& $ul ': {
                    listStyleType: 'square',
                    listStylePosition: 'outside',
                    '& li': {
                        marginLeft: theme.spacing[72]
                    }
                }
            }
        },
        '& $ol ': {
            listStylePosition: 'outside',
            listStyleType: 'number',
            '& li': {
                marginLeft: theme.spacing[24]
            },
            '& $ol ': {
                listStylePosition: 'outside',
                '& li': {
                    marginLeft: theme.spacing[48]
                },
                '& $ol ': {
                    listStylePosition: 'outside',
                    '& li': {
                        marginLeft: theme.spacing[72]
                    }
                }
            }
        },
        fontSize: theme.fontSize.md,
        lineHeight: theme.lineHeight.normal,
        fontWeight: theme.fontWeight.normal,
        color: theme.colors.grey00,
        extend: ({ breakpoint }) => {
            switch (breakpoint) {
                case 'lg':
                case 'md': return {
                    paddingLeft: theme.spacing[16]
                };
                default: return {
                    paddingLeft: '0.4rem'
                };
            }
        }
    },
    bordered: {
        marrginBottom: theme.spacing[12],
        height: theme.spacing[48],
        extend: ({ breakpoint }) => {
            switch (breakpoint) {
                case 'xs': return {
                    width: '100%'
                };
                default: return {
                    width: '50%'
                };
            }
        }
    }
}));

const _AboutThisPolicy = ({ className, config, quote, productSpecialConditions, productInclusions, productBenefits, productOptionalExtra, productDisclosures }) => {
    const theme = useContext(ThemeContext);
    const breakpoint = getBreakpointFromManor();
    const classes = useStyles({ theme, breakpoint });
    const { id, label } = config;
    let productSpecialConditionsAvailable = true;
    if (!productSpecialConditions) { productSpecialConditionsAvailable = false; }
    if (productSpecialConditions === '<ul><li>Refer to PDS</li></ul>') { productSpecialConditionsAvailable = false; }  // ERIC remove render

    return (
        <div id={id} className={classNames(classes.container, className)}>
            <SectionTitle label={label} />
            {productSpecialConditionsAvailable ? (
                <ContentCard>
                    <ContentTitleText title="Special Conditions" />
                    <div
                        dangerouslySetInnerHTML={{ __html: productSpecialConditions }}
                        className={classNames(classes.content)}
                    />
                </ContentCard>
            ) : null}
            <ContentCard>
                <ContentTitleText title="What's Included" />
                <div
                    dangerouslySetInnerHTML={{ __html: productInclusions }}
                    className={classNames(classes.content)}
                />
                <div
                    dangerouslySetInnerHTML={{ __html: productBenefits }}
                    className={classNames(classes.content)}
                />
            </ContentCard>
            <ContentCard>
                <ContentTitleText title="Optional Extras" subTitle="The following Optional Extras are available to be selected on the provider's site when purchasing this policy:" />
                <div
                    dangerouslySetInnerHTML={{ __html: productOptionalExtra }}
                    className={classNames(classes.content)}
                />
            </ContentCard>
            <ContentCard>
                <ContentTitleText title="Product Documents" subTitle="Read the PDS and other product documents to determine if this product meets your needs." />
                <Button
                    trackingLabel="Product Documents button"
                    variant="secondary"
                    icon={faFileAlt}
                    className={classes.bordered}
                    iconAlign="right"
                    href={productDisclosures.get(0).get('url')}
                >
                    Product Disclosure Statement
                </Button>

                <IsThisProductSuitableForMe quote={quote}>
                    <Button
                        trackingLabel="Product suitability button"
                        variant="secondary"
                        icon={faList}
                        className={classes.bordered}
                        iconAlign="right"
                    >
                        Product Suitability
                    </Button>

                </IsThisProductSuitableForMe>
            </ContentCard>
        </div>
    );
};

_AboutThisPolicy.propTypes = {
    className: PropTypes.string,
    config: PropTypes.object.isRequired,
    quote: PropTypes.object.isRequired,
    productSpecialConditions: PropTypes.string.isRequired,
    productInclusions: PropTypes.string.isRequired,
    productOptionalExtra: PropTypes.string.isRequired,
    productDisclosures: PropTypes.object.isRequired,
    productBenefits: PropTypes.string.isRequired
};

const AboutThisPolicy = withQuoteFlatten(_AboutThisPolicy);

export { AboutThisPolicy };
